import { CheckedState } from '@radix-ui/react-checkbox';
import { MapPin, Wallpaper } from 'lucide-react';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import { RichText, SerializableAnnotation } from '../../../types';
import { contentfulRichText, toRounded } from '../../../utils/helper';
import { Button } from '../../shadcn-ui/button';
import { Checkbox } from '../../shadcn-ui/checkbox';
import {
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../shadcn-ui/dialog';
import { Input } from '../../shadcn-ui/input';
import Textarea from '../../shared/Textarea';
import { Label } from '../../shadcn-ui/label';
import { Separator } from '../../shadcn-ui/separator';
import RichTextEditor from '../../shared/RichTextEditor';
import RichTextEditor2 from '../../shared/RichTextEditor2';
import { ChangeEvent, ReactNode, useMemo } from "react";


export type AnnotationEditorOnChange = (
  key: 'title' | 'description' | 'description_short' | 'descriptionVisible',
  value: string | RichText | RichText | boolean
) => void;

type AnnotationEditorProps = {
  annotation: Pick<SerializableAnnotation, 'title' | 'description' | 'description_short'> &
    Partial<
      Pick<
        SerializableAnnotation,
        'position' | 'cameraPosition' | 'descriptionVisible'
      >
    >;
  onChange: AnnotationEditorOnChange;
  submitLabel: 'create' | 'update';
  onSubmit: React.MouseEventHandler<HTMLButtonElement>;
  onCancel: React.MouseEventHandler<HTMLButtonElement>;
};

const _formatNumbers = (n: number[] | undefined): string => {
  if (!n) {
    return '(-, -, -)';
  }
  const joined = n.map(toRounded).join(', ');
  return `(${joined})`;
};

const AnnotationEditor: React.FC<AnnotationEditorProps> = ({
  annotation,
  onChange,
  onSubmit,
  submitLabel,
  onCancel,
}) => {
  const { t } = useTranslation();

  const _setTitle = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      onChange('title', e.target.value);
    },
    [onChange]
  );

  const _setDescriptionVisible = useCallback(
    (checked: CheckedState) => {
      onChange('descriptionVisible', checked);
    },
    [onChange]
  );

  const _setDescription = useCallback(
    (value: string) => {
      if (contentfulRichText(value)) {
        onChange('description', value);
      } else {
        onChange('description', '');
      }
    },
    [onChange]
  );

  const _location = useCallback(() => {
    return _formatNumbers(annotation.position);
  }, [annotation.position]);

  const _cameraPosition = useCallback(() => {
    return _formatNumbers(annotation.cameraPosition);
  }, [annotation.cameraPosition]);
  

  const _setDescriptionShort = useCallback(
    (value: string) => {
    	
    	let counter = document.getElementsByClassName('rt_counter')[0];

    	const div = document.createElement("div");
		div.innerHTML = value;
		let text = div.textContent || div.innerText || "";
		    	
    	if (counter) counter.innerHTML = ''+(text.trim() == '' ? 0:text.length)+' / '+limit;
    	
    	if (text.length < limit) {
    	
    		if (contentfulRichText(value)) {
	    		onChange('description_short', value);
      		} else {
    	  		onChange('description_short', '');
	      	}
    	
    	} else {
    	
    		
    		let description_short_wrapper = document.getElementsByClassName('description_short_wrapper')[0];
    		var ql_editor = description_short_wrapper.getElementsByClassName('ql-editor')[0];    		
    		
			const div = document.createElement("div");
			div.innerHTML = value;
			let text = div.textContent || div.innerText || "";
			text = text.substr(0,limit);			
			ql_editor.innerHTML = text;
			
            let setpos = document.createRange();
            let set = window.getSelection();
            setpos.setStart(ql_editor.childNodes[0], 250);
            setpos.collapse(true);
            if (set) set.removeAllRanges();
            if (set )set.addRange(setpos);

    	}

    },
    [onChange]
  );
  
  const limit = 250;
  
  console.log('annotation',annotation);
  
  return (
    <DialogContent className="light" aria-describedby="">
      <DialogHeader>
        <DialogTitle className="grid grid-cols-2 font-normal text-accent">
          <span className="flex gap-1">
            <MapPin size={16} /> <span>{_location()}</span>{' '}
          </span>
          <span className="flex gap-1">
            <Wallpaper size={16} /> <span>{_cameraPosition()}</span>
          </span>
        </DialogTitle>
        <Separator />
      </DialogHeader>

      <div className="grid gap-3 py-3">
        <Input
          value={annotation.title}
          onChange={_setTitle}
          placeholder={t('title')}
          className="bg-editor font-medium placeholder:capitalize placeholder:text-accent"
        />
        
        <div className="description_short_wrapper">
	        <RichTextEditor2
    	      value={annotation.description_short}
        	  onChange={_setDescriptionShort}
	        />
	        <div className="rt_counter">{annotation.description_short.length - 7 > 0 ? annotation.description_short.length - 7:0} / {limit}</div>
        </div>

        <RichTextEditor
          value={annotation.description}
          onChange={_setDescription}
        />

        {/* Transition duration */}
        <div className="flex items-center gap-2">
          <Checkbox
            id="slideEditor_transitionDuration"
            checked={annotation.descriptionVisible}
            onCheckedChange={_setDescriptionVisible}
          />
          <Label
            htmlFor="slideEditor_transitionDuration"
            className="font-normal text-foreground"
          >
            {t('annotations.open_on_enter')}
          </Label>
        </div>
      </div>

      <DialogFooter className="flex gap-4">
        <DialogClose asChild>
          <Button onClick={onCancel} variant="outline" className="capitalize">
            {t('cancel')}
          </Button>
        </DialogClose>
        <DialogClose asChild>
          <Button
            type="submit"
            onClick={onSubmit}
            className="bg-primary capitalize text-primary-foreground"
          >
            {t(submitLabel)}
          </Button>
        </DialogClose>
      </DialogFooter>
    </DialogContent>
  );
};

export default AnnotationEditor;
